import { useMemo } from 'react';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import type { ShareActionsOptions } from '#src/app/shared/hooks/share';
import { useNativeShareTitle } from '#src/app/shared/hooks/share';

import { useGetShareContent } from './content';
import { useOnShareFailure } from './failure';
import type { useOnShareSuccess } from './success';
import { useGetShareTrackingData } from './tracking';

type ShareSuccess = Pick<ReturnType<typeof useOnShareSuccess>, 'onSuccess' | 'onShareTapped'>;

/** Takes the success handlers, so the caller keeps the same `useRedirect` instance for the QR redirect. */
export function useShareOptions({ onSuccess, onShareTapped }: ShareSuccess): ShareActionsOptions {
	const getContent = useGetShareContent();
	const getTrackingData = useGetShareTrackingData();
	const onFailure = useOnShareFailure();
	const {
		data: {
			nativeShareTitleExperiment,
			petition: { displayTitle },
		},
	} = usePsfPromoteOrSharePageData();
	const mobileNativeShare = useNativeShareTitle({
		experiment: nativeShareTitleExperiment,
		surface: 'psf_promote_or_share',
		petitionTitle: displayTitle,
	});

	return useMemo(
		() => ({
			trackingEventName: 'share_petition',
			getContent,
			getTrackingData,
			// Tapping any channel while the copy-link redirect is pending keeps the user here instead of
			// yanking them to the share page mid-action (CHANGE-97962). Only visible for the channels
			// whose education modal needs a second tap (instagram/instagramStories/tiktok) — every other
			// channel redirects immediately on its own success, which supersedes the pending one anyway.
			onBeforeShare: onShareTapped,
			onSuccess,
			onFailure,
			config: {
				facebook: { mode: { mobile: 'url', desktop: 'sdk' } }, // Disable Facebook SDK by default on mobile to avoid a bug with the share dialog. See https://change.atlassian.net/browse/CHANGE-75410
				mobileNativeShare,
			},
		}),
		[getContent, getTrackingData, onShareTapped, onSuccess, onFailure, mobileNativeShare],
	);
}
